import React, { useEffect, useMemo, useState } from "react";
import { ThemeBasedColorHeader } from "src/shared/modules/profile/theme/themeCheacker";
import { Order } from "src/shared/types";
import { ReverseAmounts } from "../hooks/useReturnCalculation";

type RefundType = "Cash" | "Card" | "Adj.Outstanding";

interface Props {
  order: Order | null;
  totals: any;
  reverse: ReverseAmounts;
  setReverse: (val: ReverseAmounts) => void;
  refundType: RefundType;
  setRefundType: (val: RefundType) => void;
}

// Every row shares the same label / value columns so amounts and inputs align on one right edge
const Row = ({ label, className = "", children }: { label: string; className?: string; children: React.ReactNode }) => (
  <div className={`flex items-center justify-between gap-2 px-2 py-1 rounded ${className}`}>
    <span>{label}</span>
    {children}
  </div>
);

const VALUE_COL = "w-28 shrink-0 text-right";

export default function ReturnSummaryCard({ order, totals, reverse, setReverse, refundType, setRefundType }: Props) {

  const [disable, setDisable] = useState<boolean>(false)
  // ✅ Get the ORIGINAL order (ORDER_CREATED) and RETURN orders
  const originalOrder = order?.orders?.find((o) => o.paymentSource === "ORDER_CREATED") || order?.orders?.[0];
  const returnOrders = order?.orders?.filter((o) => o.paymentSource === "RETURN") || [];

  // ✅ Calculate total reversed amounts from previous returns
  let totalReversedDiscount = 0;
  let totalReversedParcelFee = 0;
  let totalReversedDeliveryFee = 0;

  returnOrders.forEach((o) => {
    totalReversedDiscount += Number(o.discount || 0);
    totalReversedParcelFee += Number(o.parcelFee || 0);
    totalReversedDeliveryFee += Number(o.deliveryFee || 0);
  });

  // ✅ Calculate maximum values for reverse fields (original - already reversed)
  const maxDiscount = Math.max(0, Number(originalOrder?.discount || 0) - totalReversedDiscount);
  const maxParcelFee = Math.max(0, Number(originalOrder?.parcelFee || 0) - totalReversedParcelFee);
  const maxDeliveryFee = Math.max(0, Number(originalOrder?.deliveryFee || 0) - totalReversedDeliveryFee);

  const refundOptions = useMemo(() => {
    const hasRefund = totals?.refund > 0;
    const hasAdjustment = totals?.adjusted > 0;

    if (hasRefund && !hasAdjustment) return ["Cash", "Card"];
    if (!hasRefund && hasAdjustment) return ["Adj.Outstanding"];
    if (hasRefund && hasAdjustment) return ["Cash", "Card"];
    return ["Cash"];
  }, [totals?.adjusted, totals?.refund]);

  // ✅ Check if any product is selected for return

  const orderLine = order?.orders?.[0];
  const checkReturnAmount = orderLine?.orderAmount === totals?.amount;

  useEffect(() => {
    if (checkReturnAmount) {
      setReverse({
        deliveryFee: Number(orderLine?.deliveryFee ?? 0),
        discount: Number(orderLine?.discount ?? 0),
        parcelFee: Number(orderLine?.parcelFee ?? 0),
      });
      setDisable(true);
    } else {
      setReverse({ deliveryFee: 0, discount: 0, parcelFee: 0 });
      setDisable(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkReturnAmount, orderLine?.deliveryFee, orderLine?.discount, orderLine?.parcelFee]);



  // ✅ Use calculated max values for reverse fields
  const reverseFields: { key: keyof ReverseAmounts; label: string; max: number }[] = [
    { key: "discount", label: "Reverse Discount", max: maxDiscount },
    { key: "parcelFee", label: "Reverse Parcel Fee", max: maxParcelFee },
    { key: "deliveryFee", label: "Reverse Delivery Fee", max: maxDeliveryFee },
  ];

  // Keep the selected method valid — the options change as the refund/adjustment split changes
  useEffect(() => {
    if (!refundOptions.includes(refundType)) setRefundType(refundOptions[0] as RefundType);
  }, [refundOptions, refundType, setRefundType]);

  if (!order) return null;

  return (
    <div className="bg-white rounded shadow-md border border-gray-100">
      <div className={`${ThemeBasedColorHeader()} text-white px-4 py-1 rounded-t font-semibold text-sm`}>Return Summary</div>

      <div className="p-2 text-sm space-y-1">
        <Row label="Total Qty">
          <span className={VALUE_COL}>{totals?.qty || 0}</span>
        </Row>

        <Row label="Total Amount">
          <span className={VALUE_COL}>QR {Number(totals?.amount || 0).toFixed(2)}</span>
        </Row>

        {reverseFields
          .filter((field) => field.max > 0)
          .map((field) => (
            <Row key={field.key} label={field.label}>
              <input
                type="number"
                value={reverse[field.key] === 0 ? "" : reverse[field.key]}
                min={0}
                max={field.max}
                placeholder="0"
                onChange={(e) => {
                  const val = e.target.value === "" ? 0 : Number(e.target.value || 0);
                  if (val >= 0 && val <= field.max) setReverse({ ...reverse, [field.key]: val });
                }}
                className={`${VALUE_COL} border rounded px-2 py-1`}
                disabled={disable}
              />
            </Row>
          ))}

        <Row label="Adj.Outstanding" className="bg-red-50 text-red-600 font-semibold">
          <span className={`${VALUE_COL} font-bold`}>QR {Number(totals?.adjusted || 0).toFixed(2)}</span>
        </Row>

        <Row label="Refund" className="bg-green-50 font-semibold">
          <span className={`${VALUE_COL} text-green-600 font-bold`}>QR {Number(totals?.refund || 0).toFixed(2)}</span>
        </Row>

        <div className="px-2 pt-1">
          <label className="text-xs">Refund Method</label>
          <select
            value={refundType}
            onChange={(e) => setRefundType(e.target.value as RefundType)}
            className={`w-full mt-1 px-2 py-2 border rounded bg-white `}
            disabled={false}
          >
            {refundOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}